import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { formatCurrency } from '../utils/helpers';
import { Trophy, Calendar, DollarSign, Users, ChevronDown } from 'lucide-react';

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCrew, setExpandedCrew] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await api.get('/api/analytics/crew');
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 lg:p-8" data-testid="analytics-loading">
          <div className="h-9 w-44 bg-black/5 rounded-2xl animate-pulse mb-8" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8">
            {[0, 1, 2, 3].map(i => <div key={i} className="card p-6 h-28 animate-pulse" />)}
          </div>
          <div className="card p-6 h-64 animate-pulse" />
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="p-6 lg:p-8">
          <h1 className="text-4xl font-serif mb-2">Analytics</h1>
          <p className="text-muted-foreground">Failed to load analytics data</p>
        </div>
      </Layout>
    );
  }

  const { crew_stats, monthly_events, event_types, totals } = data;
  const topCrew = crew_stats.length > 0 ? crew_stats[0] : null;

  // Only show INR when someone has actually been PAID in INR (not auto-calculated pending)
  const hasAnyINR = totals.total_paid_inr > 0 || crew_stats.some(c => c.total_earned_inr > 0);

  const busiestMonth = Object.entries(monthly_events).length > 0
    ? Object.entries(monthly_events).reduce((a, b) => (a[1] > b[1] ? a : b))
    : null;

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const monthEntries = Object.entries(monthly_events);
  const maxMonthEvents = monthEntries.length > 0
    ? Math.max(...monthEntries.map(([, v]) => v))
    : 1;

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="analytics-page">
        <div className="mb-8">
          <h1 className="text-4xl font-serif mb-2">Analytics</h1>
          <p className="text-muted-foreground">Crew performance and business insights</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card p-6" data-testid="stat-total-events">
            <Calendar className="text-gold mb-3" size={24} />
            <p className="text-3xl font-serif">{totals.total_events}</p>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">Total Events</p>
          </div>
          <div className="card p-6" data-testid="stat-total-assignments">
            <Users className="text-gold mb-3" size={24} />
            <p className="text-3xl font-serif">{totals.total_assignments}</p>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">Total Crew Days</p>
          </div>
          <div className="card p-6" data-testid="stat-paid">
            <DollarSign className="text-green-600 dark:text-green-400 mb-3" size={24} />
            <p className="text-3xl font-serif">{formatCurrency(totals.total_paid_usd, 'USD')}</p>
            {hasAnyINR && <p className="text-sm text-muted-foreground">{formatCurrency(totals.total_paid_inr, 'INR')}</p>}
            <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">Total Paid</p>
          </div>
          <div className="card p-6" data-testid="stat-pending">
            <DollarSign className="text-amber-600 dark:text-amber-400 mb-3" size={24} />
            <p className="text-3xl font-serif text-amber-600 dark:text-amber-400">{formatCurrency(totals.total_pending_usd, 'USD')}</p>
            {hasAnyINR && <p className="text-sm text-amber-600 dark:text-amber-400">{formatCurrency(totals.total_pending_inr, 'INR')}</p>}
            <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">Pending Payment</p>
          </div>
        </div>

        {/* MVP Crew & Busiest Month */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {topCrew && (
            <div className="card p-4 sm:p-6" data-testid="mvp-crew">
              <div className="flex items-center gap-3 mb-4">
                <Trophy className="text-gold flex-shrink-0" size={28} />
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Most Active Crew Member</p>
                  <h2 className="text-xl sm:text-2xl font-serif truncate">{topCrew.name}</h2>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="p-2 sm:p-3 bg-muted text-center">
                  <p className="text-xl sm:text-2xl font-serif">{topCrew.total_events}</p>
                  <p className="text-xs text-muted-foreground">Events</p>
                </div>
                <div className="p-2 sm:p-3 bg-muted text-center">
                  <p className="text-lg sm:text-2xl font-serif">{formatCurrency(topCrew.total_earned_usd, 'USD')}</p>
                  <p className="text-xs text-muted-foreground">Earned</p>
                </div>
                <div className="p-2 sm:p-3 bg-muted text-center overflow-hidden">
                  <p className="text-sm sm:text-2xl font-serif truncate">{topCrew.role}</p>
                  <p className="text-xs text-muted-foreground">Role</p>
                </div>
              </div>
            </div>
          )}

          {busiestMonth && (
            <div className="card p-6" data-testid="busiest-month">
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="text-gold" size={28} />
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Busiest Month</p>
                  <h2 className="text-2xl font-serif">{formatMonth(busiestMonth[0])}</h2>
                </div>
              </div>
              <p className="text-4xl font-serif text-gold">{busiestMonth[1]} events</p>
            </div>
          )}
        </div>

        {/* Crew Leaderboard */}
        <div className="card p-4 sm:p-6 mb-8" data-testid="crew-leaderboard">
          <h2 className="text-2xl font-serif mb-6">Crew Leaderboard</h2>
          <div className="space-y-3">
            {crew_stats.map((crew, index) => {
              const crewHasINR = crew.total_earned_inr > 0;
              return (
                <div key={crew.crew_id}>
                  <div
                    className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 border border-border hover:border-gold/50 transition-colors cursor-pointer"
                    onClick={() => setExpandedCrew(expandedCrew === crew.crew_id ? null : crew.crew_id)}
                    data-testid={`crew-stat-${crew.crew_id}`}
                  >
                    {/* Rank + Name */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center font-serif text-lg ${
                        index === 0 ? 'bg-primary text-primary-foreground' :
                        index === 1 ? 'bg-gray-300 text-foreground' :
                        index === 2 ? 'bg-amber-700 text-white' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-base sm:text-lg truncate">{crew.name}</p>
                        <p className="text-sm text-muted-foreground">{crew.role}</p>
                      </div>
                    </div>
                    
                    {/* Stats Row */}
                    <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6 pl-11 sm:pl-0">
                      <div className="text-center">
                        <p className="text-xl sm:text-2xl font-serif">{crew.total_events}</p>
                        <p className="text-xs text-muted-foreground">Events</p>
                      </div>
                      <div className="text-center sm:text-right">
                        <p className="text-base sm:text-lg">{formatCurrency(crew.total_earned_usd, 'USD')}</p>
                        <p className="text-xs text-muted-foreground">Earned</p>
                      </div>
                      <div className="text-center sm:text-right">
                        <p className="text-base sm:text-lg text-amber-600 dark:text-amber-400">{formatCurrency(crew.total_pending_usd, 'USD')}</p>
                        <p className="text-xs text-muted-foreground">Pending</p>
                      </div>
                      <ChevronDown size={20} className={`hidden sm:block flex-shrink-0 transition-transform ${expandedCrew === crew.crew_id ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {expandedCrew === crew.crew_id && (
                    <div className="border border-t-0 border-border p-4 bg-muted/30" data-testid={`crew-detail-${crew.crew_id}`}>
                      <div className="grid grid-cols-1 gap-6">
                        {/* Monthly Breakdown */}
                        <div>
                          <h4 className="text-sm uppercase tracking-wide text-muted-foreground mb-3">Monthly Breakdown</h4>
                          {Object.keys(crew.monthly_earnings).length > 0 ? (
                            <div className="space-y-2">
                              {Object.entries(crew.monthly_earnings).map(([month, vals]) => (
                                <div key={month} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-2 bg-background border border-border gap-1 sm:gap-4">
                                  <span className="text-sm font-medium">{formatMonth(month)}</span>
                                  <div className="flex items-center gap-4 text-sm">
                                    <span className="text-xs text-muted-foreground">{vals.events} event{vals.events !== 1 ? 's' : ''}</span>
                                    <span>{formatCurrency(vals.usd, 'USD')}</span>
                                    {vals.inr > 0 && <span className="text-xs text-muted-foreground">{formatCurrency(vals.inr, 'INR')}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">No data yet</p>
                          )}
                        </div>

                        {/* Yearly Summary */}
                        <div>
                          <h4 className="text-sm uppercase tracking-wide text-muted-foreground mb-3">Yearly Summary</h4>
                          {Object.keys(crew.yearly_earnings).length > 0 ? (
                            <div className="space-y-2">
                              {Object.entries(crew.yearly_earnings).map(([year, vals]) => (
                                <div key={year} className="p-4 bg-background border border-border">
                                  <p className="text-xl font-serif mb-2">{year}</p>
                                  <div className={`grid gap-2 text-center ${vals.inr > 0 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                                    <div>
                                      <p className="text-lg font-serif">{vals.events}</p>
                                      <p className="text-xs text-muted-foreground">Events</p>
                                    </div>
                                    <div>
                                      <p className="text-lg">{formatCurrency(vals.usd, 'USD')}</p>
                                      <p className="text-xs text-muted-foreground">USD</p>
                                    </div>
                                    {vals.inr > 0 && (
                                      <div>
                                        <p className="text-lg">{formatCurrency(vals.inr, 'INR')}</p>
                                        <p className="text-xs text-muted-foreground">INR</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">No data yet</p>
                          )}

                          {/* Totals */}
                          <div className="mt-4 p-3 border-t border-border">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Total Earned</span>
                              <span className="font-medium">{formatCurrency(crew.total_earned_usd, 'USD')}</span>
                            </div>
                            {crewHasINR && crew.total_earned_inr > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Total Earned (INR)</span>
                                <span className="font-medium">{formatCurrency(crew.total_earned_inr, 'INR')}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-sm mt-2">
                              <span className="text-amber-600 dark:text-amber-400">Pending</span>
                              <span className="text-amber-600 dark:text-amber-400 font-medium">{formatCurrency(crew.total_pending_usd, 'USD')}</span>
                            </div>
                            {crewHasINR && crew.total_pending_inr > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-amber-600 dark:text-amber-400">Pending (INR)</span>
                                <span className="text-amber-600 dark:text-amber-400 font-medium">{formatCurrency(crew.total_pending_inr, 'INR')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {crew_stats.length === 0 && (
              <p className="text-muted-foreground text-center py-8">No crew data yet. Assign crew to events to see analytics.</p>
            )}
          </div>
        </div>

        {/* Events per Month + Event Types */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="card p-6" data-testid="events-per-month">
            <h2 className="text-2xl font-serif mb-6">Events per Month</h2>
            {monthEntries.length > 0 ? (
              <div className="space-y-3">
                {monthEntries.slice(-12).map(([month, count]) => (
                  <div key={month} className="flex items-center gap-3">
                    <span className="text-sm w-24 text-muted-foreground">{formatMonth(month)}</span>
                    <div className="flex-1 bg-muted h-8 relative">
                      <div
                        className="bg-gold h-8 transition-all duration-500"
                        style={{ width: `${(count / maxMonthEvents) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-8 text-right">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No event data yet</p>
            )}
          </div>

          <div className="card p-6" data-testid="event-types">
            <h2 className="text-2xl font-serif mb-6">Event Types</h2>
            {Object.keys(event_types).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(event_types)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => {
                    const totalEvents = Object.values(event_types).reduce((a, b) => a + b, 0);
                    const pct = ((count / totalEvents) * 100).toFixed(0);
                    return (
                      <div key={type} className="flex items-center justify-between p-3 border border-border">
                        <div className="flex items-center gap-3">
                          <span className="w-3 h-3 bg-gold" style={{ opacity: 0.4 + (count / totalEvents) * 0.6 }} />
                          <span className="font-medium">{type}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-muted-foreground">{count} event{count !== 1 ? 's' : ''}</span>
                          <span className="text-sm font-medium w-10 text-right">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No event data yet</p>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Analytics;
